/**
 * Auth Context
 * 
 * Contexto de React para manejar el estado de autenticación
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { AuthUser } from '../lib/auth'
import { getCurrentAuthUser, logout as authLogout, getAuthToken } from '../lib/auth'

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<{ error?: Error; nextStep?: string }>
  completeNewPassword: (newPassword: string) => Promise<{ error?: Error }>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  getToken: () => Promise<string | null>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Development mode - bypass auth for local UI development
  // Access import.meta.env directly so Vite can replace at compile time
  const isDevelopmentMode = import.meta.env?.MODE === 'development' &&
    (import.meta.env?.VITE_USER_POOL_ID?.includes('PLACEHOLDER') ||
     import.meta.env?.VITE_DEV_MODE === 'true')

  const checkAuth = useCallback(async () => {
    try {
      // In development mode, auto-set mock user without Cognito
      if (isDevelopmentMode) {
        console.log('🔧 Development Mode: Auto-authenticating mock user')
        setUser({
          userId: 'dev-user-123',
          email: 'admin@trafilea.com',
          tenantId: 'trafilea',
          roles: ['admin', 'user']
        })
        setIsLoading(false)
        return
      }
      // Intentar refrescar la sesión con forceRefresh si es necesario
      const session = await fetchAuthSession({ forceRefresh: false })
      if (session.tokens) {
        const currentUser = await getCurrentAuthUser()
        if (currentUser) {
          setUser(currentUser)
        } else {
          console.warn('[AuthContext] getCurrentAuthUser returned null despite having session tokens')
          // Si hay tokens pero getCurrentAuthUser retorna null, 
          // intentamos crear un usuario básico con la información de la sesión
          const userId = session.tokens.idToken?.payload?.sub as string | undefined
          const email = session.tokens.idToken?.payload?.email as string | undefined
          const tenantId = session.tokens.idToken?.payload?.['custom:tenant_id'] as string | undefined ||
                          session.tokens.accessToken?.payload?.['custom:tenant_id'] as string | undefined
          
          // Extraer roles de los tokens (verificar tanto accessToken como idToken)
          const groupsValue = session.tokens?.accessToken?.payload?.['cognito:groups'] ||
                            session.tokens?.idToken?.payload?.['cognito:groups']
          let roles: string[] = []
          if (Array.isArray(groupsValue)) {
            roles = groupsValue as string[]
          } else if (typeof groupsValue === 'string') {
            roles = groupsValue.split(',').map((g: string) => g.trim())
          }
          
          // Si no hay grupos, verificar custom:role
          if (roles.length === 0) {
            const customRole = session.tokens?.accessToken?.payload?.['custom:role'] as string | undefined ||
                             session.tokens?.idToken?.payload?.['custom:role'] as string | undefined
            if (customRole) {
              roles = [customRole]
            }
          }
          
          if (userId && email) {
            setUser({
              userId,
              email,
              tenantId: tenantId || undefined,
              roles,
            })
          } else {
            setUser(null)
          }
        }
      } else {
        setUser(null)
      }
    } catch (error: any) {
      console.error('[AuthContext] Error checking auth:', error)
      // Si el error es de token expirado, intentar refrescar
      if (error?.name === 'NotAuthorizedException' || error?.message?.includes('token')) {
        try {
          console.log('[AuthContext] Attempting to refresh session...')
          const refreshedSession = await fetchAuthSession({ forceRefresh: true })
          if (refreshedSession.tokens) {
            const currentUser = await getCurrentAuthUser()
            if (currentUser) {
              setUser(currentUser)
              return
            }
          }
        } catch (refreshError) {
          console.error('[AuthContext] Error refreshing session:', refreshError)
        }
      }
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [isDevelopmentMode])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    try {
      // In development mode, mock successful login
      if (isDevelopmentMode) {
        console.log('🔧 Development Mode: Mock login successful')
        setUser({
          userId: 'dev-user-123',
          email: email,
          tenantId: 'trafilea',
          roles: ['admin', 'user']
        })
        setIsLoading(false)
        return {}
      }

      const { login: authLogin } = await import('../lib/auth')
      const result = await authLogin(email, password)
      if (result.nextStep === 'DONE' || !result.nextStep) {
        await checkAuth()
      }
      return result
    } catch (error: any) {
      return { error }
    } finally {
      setIsLoading(false)
    }
  }, [checkAuth])

  const completeNewPassword = useCallback(async (newPassword: string) => {
    setIsLoading(true)
    try {
      const { confirmNewPassword } = await import('../lib/auth')
      const result = await confirmNewPassword(newPassword)
      if (!result.error) {
        await checkAuth()
      }
      return result
    } catch (error: any) {
      return { error }
    } finally {
      setIsLoading(false)
    }
  }, [checkAuth, isDevelopmentMode])

  const logout = useCallback(async () => {
    setIsLoading(true)
    try {
      await authLogout()
      setUser(null)
    } catch (error) {
      console.error('Error logging out:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshUser = useCallback(async () => {
    await checkAuth()
  }, [checkAuth])

  const getToken = useCallback(async () => {
    return await getAuthToken()
  }, [])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    completeNewPassword,
    logout,
    refreshUser,
    getToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}












